import composeStore from '../helpers/generate-pagination-data-store'
import resetStore from '../helpers/reset-store'
import composeParams from '../helpers/store-helpers/compose-params'
import axios from "axios";
import _ from "lodash";
import getProductImages from '@/helpers/get-product-images';


const initialStore = composeStore(
  'products',
  ['listing', 'search'],
  '/api/item',
  composeParams
)

export const state = () => ({
  ...initialStore.state,
  firstBanner: [],
  secondBanner: [],
  fbtProducts: [],
  altProducts: []
})

export const mutations = {
  ...initialStore.mutation,
  reset(state) {
    Object.keys(state).forEach((key) => {
      // Reset arrays to empty, objects to {}, and primitives to null
      if (Array.isArray(state[key])) {
        state[key] = [];
      } else if (typeof state[key] === 'object' && state[key] !== null) {
        state[key] = {};
      } else {
        state[key] = null;
      }
    });
  },
  setFirstBanner(state, products) {
    state.firstBanner = products
  },
  setSecondBanner(state, products) {
    state.secondBanner = products
  },
  setFbtProducts(state, products) {
    state.fbtProducts = products
  },
  setAltProducts(state, products) {
    state.altProducts = products
  }
}

export const getters = {
  ...initialStore.getter,
  firstBanner(state) {
    return state.firstBanner
  },
  secondBanner(state) {
    return state.secondBanner
  },
  fbtProducts(state){
    return state.fbtProducts
  },
  altProducts(state){
    return state.altProducts
  }
}

function getProductCategory(product) {
  const fieldNumbers = [10, 9, 8, 7, 6, 5, 4, 3]
  for (const num of fieldNumbers) {
    const padded = String(num).padStart(2, '0')
    const name = _.get(product, 'custitem_smi_add_prod_categ_' + padded + '.value.name')
    if (name) {
      return name
    }
  }
  return 'Unknown Category'
}

function formatBannerProducts(products) {
  return products.map(product => {

    // const isInStock = product.simples ? product.simples.some(simple => simple.instock) : false;

    return {
      chosenColor: '',
      colors: product.colors,
      name: product.displayName,
      category: getProductCategory(product),
      shortDesc: '', // Placeholder for now
      fromPrice: parseFloat(product.price),
      productCode: product.itemId,
      itemId: product.itemId,
      inStock: product.instock,
      hasFinish: product.hasFinish,
      nextDayDel: true,
      enStandards: _.uniq([
        _.get(product, `custitem_bbs_enstandard1.value.name`),
        _.get(product, `custitem_bbs_enstandard3.value.name`),
        _.get(product, `custitem_bbs_standard2.value.name`)
      ].filter(Boolean)),  // Remove falsy values and duplicates
      image: product.custitem_bbs_default_product_image?.value || product.custitem_bbs_normal_webpath_via_s3frnt?.value  || "/images/smi-logo.svg",
      hoverImage: getProductImages(product)[1] || null,
      slug: product.slug,
      link: '/product/' + product.slug,
      isEcoRange: ['yes', 'true'].includes(String(product?.custitem_smi_sust_range?.value).toLowerCase()),
      // Quick add is only offered on products with no variations, matching the
      // listing views (ProductList / ProductListV2 / SearchResults / LandingPage).
      showQuickAdd: !product.simples || product.simples.length === 0,
      qty: product.qty || 0,
      productId: product._id,
      budget: product.budget,
      budgetType: product.budgetType,
      budgets: product.budgets,
      pointsMultiplier: product.pointsMultiplier,
      price: product.price,
      features: [
        { heading: product.custitem_smi_feature_bullet_heading_1, subheading: product.custitem_smi_feature_bullet_subhead_1 },
        { heading: product.custitem_smi_feature_bullet_heading_2, subheading: product.custitem_smi_feature_bullet_subhead_2 },
        { heading: product.custitem_smi_feature_bullet_heading_3, subheading: product.custitem_smi_feature_bullet_subhead_3 }
      ]
    };
  });
}


export const actions = {
  ...initialStore.action,
  async getFirstBanner({ commit }, payload = {}) {
    const bannerSize = 8;
    try {
      const sectorProductData = payload.accountInfo?.sector?.custrecord_smi_sector_product?.value;
      let productIds = [];
      let finalProducts = [];

      if (sectorProductData && sectorProductData.length > 0) {
        productIds = sectorProductData.map(product => product.$attributes.internalId);
      }
      if (productIds.length > bannerSize) {
        productIds = productIds.sort(() => 0.5 - Math.random()).slice(0, bannerSize);
      } 
      const baseParams = {
        includePricing: true,
        includeChildren: true,
        includeBudget: true,
        scope: 'full',
      };
      if (productIds.length > 0) {
        const response = await axios.get('/api/item', {
          params: { ...baseParams, $limit: productIds.length, 'integerId[$in]': productIds }
        });
        if (response.data?.data) {
          finalProducts = formatBannerProducts(response.data.data);
        }
      }
      const needed = bannerSize - finalProducts.length;
      if (needed > 0) {
        const fillerResponse = await axios.get('/api/item', {
          params: { ...baseParams, $limit: needed }
        });
        if (fillerResponse.data?.data) {
          const fillerProducts = formatBannerProducts(fillerResponse.data.data);
          // Add the new products to the list
          finalProducts.push(...fillerProducts);
        }
      }

      // This final check ensures we have no duplicates and the final count is correct.
      const uniqueProducts = [];
      const seenIds = new Set();
      for (const product of finalProducts) {
        if (!seenIds.has(product.productCode)) {
          uniqueProducts.push(product);
          seenIds.add(product.productCode);
        }
      }

      commit('setFirstBanner', uniqueProducts.slice(0, bannerSize));

    } catch (error) {
      console.error("Error fetching first banner products:", error);
      commit('setFirstBanner', []); // Clear the banner on error
    }
  },
  async getSecondBanner({ commit }) {
    const response = await axios.get('/api/item', {
      params: {
        $limit: 4,
        $skip: 4,
        includePricing: true,
        includeChildren: true,
        includeBudget: true,
        scope: 'full'
      }
    })

    if(response.data && response.data.data){
      const formattedProducts = formatBannerProducts(response.data.data);
      commit('setSecondBanner', formattedProducts)
    }
  },
  async getFbtProducts({ commit }, payload) {
    const { productClass, productId } = payload;
    const response = await axios.get('/api/item', {
      params: {
        $limit: 4,
        includePricing: true,
        includeChildren: true,
        includeBudget: true,
        scope: 'full',
        'class.internalId[$match]': productClass,
        'integerId[$ne]': productId
      }
    })
    if(response.data && response.data.data){
      const formattedProducts = formatBannerProducts(response.data.data);

      commit('setFbtProducts', formattedProducts)
    }
  },
  async getAltProducts({ commit }, payload) {
    const { productClass, productId, scope, catalogueId } = payload;
    
    // Use the exact same scope that was used to load the main product
    if (!scope) {
      console.warn('No scope provided for alt products, skipping');
      commit('setAltProducts', []);
      return;
    }
    
    try {
      const params = {
        $limit: 8,
        includePricing: true,
        includeChildren: true,
        includeBudget: true,
        scope: scope,
        'class.internalId[$match]': productClass,
        'integerId[$ne]': productId,
        '$or[0][custitem_bbs_item_stocked.value][$match]': true,
        '$or[1][custitem_smi_nonstock.value][$match]': true,
        '$or[2][isDropShipItem][$match]': true
      };
      
      // Add catalogueId only if it was provided (for custom_cat_new scope)
      if (catalogueId) {
        params.catalogueId = catalogueId;
      }
      
      const response = await axios.get('/api/item', { params });
      
      if(response?.data?.data?.length) {
        const formattedProducts = formatBannerProducts(response.data.data);
        commit('setAltProducts', formattedProducts);
      } else {
        commit('setAltProducts', []);
      }
    } catch (error) {
      console.error('Failed to load alt products:', error);
      commit('setAltProducts', []);
    }
  },
}

export default {
  namespaced: true, // optional, if you want namespacing
  state,
  mutations,
  actions,
  getters,
};